import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import PermissionDialog, {
  PermissionOption,
} from '../roles/PermissionDialog';
import permissionService from '../../../services/authority/permissionService';
import platformRoleService from './platformRoleService';
import { PLATFORM_PERMISSION_NAMES } from '../../../utils/adminMode';

const PlatformRoleEdit = () => {
  const params = useParams();
  const isCreate = !params.id || params.id === 'new';
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { enqueueSnackbar } = useSnackbar();
  const [entity, setEntity] = useState<any>({
    id: '',
    name: '',
    description: '',
    isSystem: false,
  });
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [allPermissions, setAllPermissions] = useState<PermissionOption[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    permissionService.list().then((response: any) => {
      const rows = Array.isArray(response)
        ? response
        : response?.records ?? [];
      const mapped = rows
        .map((x: any) =>
          typeof x === 'string'
            ? { name: x, description: x }
            : {
                id: x.id ?? x.Id,
                name: x.name ?? x.Name,
                description:
                  x.description ?? x.Description ?? x.name ?? x.Name,
              }
        )
        .filter((p: PermissionOption) => !!p.name);
      setAllPermissions(mapped);
    });
  }, []);

  useEffect(() => {
    if (isCreate) return;
    dispatch({ type: 'SHOW_QUERY' });
    platformRoleService
      .get(params.id!)
      .then((response) => {
        if (response.hasError) {
          enqueueSnackbar((response.errorMessages ?? []).join(','), {
            variant: 'error',
          });
          return;
        }
        const r = response.result ?? {};
        setEntity({
          id: r.id ?? r.Id,
          name: r.name ?? r.Name ?? '',
          description: r.description ?? r.Description ?? '',
          isSystem: r.isSystem ?? r.IsSystem ?? false,
        });
        setSelectedPermissions(r.permissions ?? r.Permissions ?? []);
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  }, [params.id]);

  const platformOptions = useMemo(() => {
    const byName = new Map(allPermissions.map((p) => [p.name!, p]));
    const names = [
      'Administrator',
      ...PLATFORM_PERMISSION_NAMES,
    ];
    return names.map(
      (name) =>
        byName.get(name) ?? {
          name,
          description: name,
        }
    );
  }, [allPermissions]);

  const onSave = () => {
    if (!entity.name?.trim()) {
      setErrors(['Név kötelező']);
      return;
    }
    dispatch({ type: 'SHOW_SAVE' });
    const payload = {
      name: entity.name.trim(),
      description: entity.description?.trim() ?? '',
    };
    const savePromise = isCreate
      ? platformRoleService.create(payload)
      : platformRoleService.update(entity.id, payload);

    savePromise
      .then(async (response) => {
        if (response.hasError) {
          setErrors(response.errorMessages ?? []);
          enqueueSnackbar((response.errorMessages ?? []).join(','), {
            variant: 'error',
          });
          return;
        }
        const id = response.result?.id ?? response.result?.Id ?? entity.id;
        if (id) {
          await platformRoleService.setPermissions(id, selectedPermissions);
        }
        enqueueSnackbar('Mentve', { variant: 'success' });
        navigate('/authority/platform-roles');
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <h2>{isCreate ? 'Új SaaS szerepkör' : 'SaaS szerepkör szerkesztése'}</h2>
          <Typography variant="body2" color="text.secondary">
            PlatformRole — kanonikus SaaS RBAC (nem Identity Role / Group).
          </Typography>
        </Grid>
        <Grid size={12}>
          <Box sx={{ color: 'red' }}>
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </Box>
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Név"
            value={entity.name}
            disabled={entity.isSystem}
            onChange={(e) => setEntity({ ...entity, name: e.target.value })}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Leírás"
            value={entity.description}
            onChange={(e) =>
              setEntity({ ...entity, description: e.target.value })
            }
          />
        </Grid>
        <Grid size={12}>
          <Button variant="outlined" onClick={() => setDialogOpen(true)}>
            Jogosultságok módosítása ({selectedPermissions.length})
          </Button>
          <PermissionDialog
            open={dialogOpen}
            permissions={platformOptions}
            selectedPermissions={selectedPermissions}
            selectionMode="name"
            onlyPlatformPermissions
            excludePlatformPermissions={false}
            title="SaaS platform jogosultságok"
            onClose={(selected) => {
              setSelectedPermissions(selected.map(String));
              setDialogOpen(false);
            }}
          />
        </Grid>
        <Grid size={12} sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={onSave}>
            Mentés
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/authority/platform-roles')}
          >
            Mégse
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PlatformRoleEdit;
